// WeChat web authorization (网页授权) on the browser side: recognising WeChat's browser and sending
// it to WeChat, which comes back to /wechat/return (docs/plans/2026-10-08-wechat-sign-in.md, §5.2).
import api from '@/api/api';

const KEY = 'slowgo.wechat';

export const settings = { appId: '', authorizeUrl: '', jsSdkUrl: '' };

export function insideWeChat() {
  return /MicroMessenger/i.test(navigator.userAgent);
}

export function available() {
  return insideWeChat() && !!settings.appId;
}

export async function loadSettings() {
  try {
    Object.assign(settings, await api.call('GET', 'wechat'));
  } catch (e) {
    // WeChat not set up on this site
  }
}

// What the return page needs across WeChat's round trips: { state, next, pending, name }.
export function saved() {
  try { return JSON.parse(sessionStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
}

export function save(values) {
  try { sessionStorage.setItem(KEY, JSON.stringify({ ...saved(), ...values })); } catch (e) { /* no storage */ }
}

export function forget() {
  try { sessionStorage.removeItem(KEY); } catch (e) { /* no storage */ }
}

function randomState() {
  const b = new Uint8Array(16);
  window.crypto.getRandomValues(b);
  return Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
}

// Sends the browser to WeChat. scope: 'snsapi_base' (silent, identity only) or 'snsapi_userinfo'
// (WeChat asks the player to allow the nickname; only from a tap, else WeChat shows a snapshot page).
// link: the browser is signed in, and the return page links this WeChat to its account.
export function authorize(scope, next, link = false) {
  const state = randomState();
  save({ state, next: next || saved().next || null, link });
  // WeChat checks the parameters strictly, in this order.
  const q = [
    `appid=${encodeURIComponent(settings.appId)}`,
    `redirect_uri=${encodeURIComponent(`${window.location.origin}/wechat/return`)}`,
    'response_type=code',
    `scope=${scope}`,
    `state=${state}`,
  ].join('&');
  window.location.assign(`${settings.authorizeUrl}?${q}#wechat_redirect`);
}

const LINK_KEY = 'slowgo.wechat.link';

// Inside WeChat, an account signed in without WeChat (email, a password reset, a session from before
// WeChat was set up) gets this WeChat linked, once per account and browser session
// (docs/plans/2026-10-16-wechat-link-signed-in.md). Returns true when it leaves for WeChat.
export async function linkSignedIn(user, next) {
  try {
    if (sessionStorage.getItem(LINK_KEY) === String(user.id)) return false;
    sessionStorage.setItem(LINK_KEY, String(user.id));
  } catch (e) {
    return false; // without storage, a failure could loop
  }
  try {
    const list = await api._rest.get_ex('wechat/identities', { params: { user: user.id } });
    if (list.length > 0) return false;
  } catch (e) {
    return false;
  }
  authorize('snsapi_base', next, true);
  return true;
}
